<template>
  <transition name="slide-up">
    <div v-if="showBanner" :class="['install-banner', darkMode ? 'darkMode' : 'lightMode']">
      <div class="banner-content">
        <div class="banner-text">
          <i class="fas fa-download banner-icon" aria-hidden="true"></i>
          <span>{{ $t('install_app_prompt') }}</span>
        </div>
        <div class="banner-buttons">
          <button @click="install" class="install-btn">
            {{ $t('install') }}
          </button>
          <button @click="dismiss" class="dismiss-btn">
            {{ $t('not_now') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'InstallBanner',
  props: {
    darkMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showBanner: false,
      deferredPrompt: null
    }
  },
  mounted() {
    // Check if app is already installed (running in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone
      || document.referrer.includes('android-app://');

    if (isStandalone) {
      return; // Don't show banner if already installed
    }

    // Check if user previously dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed === 'true') {
      return;
    }

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);

    // For iOS, show banner after a delay if not standalone
    if (this.isIOS() && !isStandalone) {
      setTimeout(() => {
        this.showBanner = true;
      }, 3000); // Show after 3 seconds
    }
  },
  beforeUnmount() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
  },
  methods: {
    handleBeforeInstallPrompt(e) {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      this.deferredPrompt = e;
      // Show the install banner
      this.showBanner = true;
    },
    async install() {
      if (!this.deferredPrompt) {
        // For iOS or if prompt not available, show instructions
        if (this.isIOS()) {
          alert(this.$t('ios_install_instructions'));
        }
        return;
      }

      // Show the install prompt
      this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;

      console.log(`User response to install prompt: ${outcome}`);

      // Clear the deferredPrompt
      this.deferredPrompt = null;

      // Hide the banner
      this.showBanner = false;
    },
    dismiss() {
      this.showBanner = false;
      // Remember dismissal for 7 days
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);
      localStorage.setItem('pwa-install-dismissed', 'true');
      localStorage.setItem('pwa-install-dismissed-expiry', expiryDate.toISOString());
    },
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }
  }
}
</script>

<style scoped>
.install-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  padding: 16px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease;
}

.install-banner.darkMode {
  background-color: #2c2c2c;
  border-top: 1px solid #444;
}

.install-banner.lightMode {
  background-color: #ffffff;
  border-top: 1px solid #e0e0e0;
}

.banner-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.banner-text {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.banner-icon {
  font-size: 24px;
  color: #3498db;
}

.banner-buttons {
  display: flex;
  gap: 8px;
}

.install-btn, .dismiss-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.install-btn {
  background-color: #3498db;
  color: white;
}

.install-btn:hover {
  background-color: #2980b9;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(52, 152, 219, 0.3);
}

.dismiss-btn {
  background-color: transparent;
  color: #666;
  border: 1px solid #ddd;
}

.darkMode .dismiss-btn {
  color: #ccc;
  border-color: #555;
}

.dismiss-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.darkMode .dismiss-btn:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .banner-content {
    flex-direction: column;
    align-items: stretch;
  }

  .banner-text {
    justify-content: center;
    text-align: center;
  }

  .banner-buttons {
    width: 100%;
  }

  .install-btn, .dismiss-btn {
    flex: 1;
  }
}

/* Transition animations */
.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
}

.slide-up-enter-to, .slide-up-leave-from {
  transform: translateY(0);
}
</style>
